import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Typography,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import { useI18n } from '../../../i18n';

const getReportTitle = (report, t) =>
  report?.reportTitle || report?.code || t('report.fallbackName', { id: report?.id });

const SealReportViewer = ({ reports, isLoadingReports, loadError }) => {
  const { t } = useI18n();

  const [selectedReportId, setSelectedReportId] = useState('');
  const [iframeUrl, setIframeUrl] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedReport = reports.find(
    (r) => r.id.toString() === selectedReportId,
  );

  const handleReportChange = (event) => {
    setSelectedReportId(event.target.value);
    setIframeUrl(null);
    setError(null);
  };

  const handleView = () => {
    if (!selectedReportId || !selectedReport) return;
    if (!selectedReport.path) {
      setError(t('reports.noPathConfigured'));
      return;
    }
    setError(null);
    setIframeLoading(true);
    // Mirrors Azuro's SealReportViewer. The dev-server proxy
    // (src/setupProxy.js) is the CRA equivalent of Azuro's Next.js
    // /api/seal-report/view/[reportId] route: it does SWILogin +
    // SWExecuteReport against Seal Report directly (forwarding the
    // browser's cookies — no .NET backend, no Bearer, no agent_id), runs
    // injectAndRewrite() on the HTML, and forwards Seal Report session
    // cookies back to the browser so subsequent iframe hops through
    // /api/seal-report/proxy/* are authenticated.
    setIframeUrl(
      `/_seal/view/${selectedReportId}?path=${encodeURIComponent(selectedReport.path)}`,
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
      >
        <FormControl sx={{ minWidth: { xs: 0, md: 300 } }}>
          <InputLabel>{t('reports.selectReport')}</InputLabel>
          <Select
            value={selectedReportId}
            label={t('reports.selectReport')}
            onChange={handleReportChange}
            disabled={isLoadingReports || reports.length === 0}
            size="small"
          >
            {reports.map((report) => (
              <MenuItem
                key={report.id}
                value={report.id.toString()}
                sx={{
                  whiteSpace: { xs: 'normal', md: 'nowrap' },
                  overflowWrap: 'anywhere',
                }}
              >
                {getReportTitle(report, t)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedReport && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              sx={{
                width: { xs: '100%', md: 'auto' },
                minHeight: { xs: 44, md: 'auto' },
              }}
              startIcon={
                iframeLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PreviewIcon />
                )
              }
              onClick={handleView}
              disabled={iframeLoading || iframeUrl !== null}
            >
              {t('reports.view')}
            </Button>
          </Stack>
        )}
      </Stack>

      {isLoadingReports && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            {t('reports.loading')}
          </Typography>
        </Box>
      )}

      {loadError && <Alert severity="error">{loadError}</Alert>}

      {!isLoadingReports && !loadError && reports.length === 0 && (
        <Alert severity="info">{t('reports.none')}</Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {iframeUrl && (
        <Card variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2">
              {getReportTitle(selectedReport, t)}
            </Typography>
          </Box>
          {iframeLoading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={100}
            >
              <CircularProgress size={32} />
            </Box>
          )}
          <Box
            sx={{
              width: '100%',
              height: { xs: '80dvh', md: 700 },
              minHeight: 400,
              display: iframeLoading ? 'none' : 'block',
            }}
          >
            <iframe
              src={iframeUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title={t('reports.frameTitle', {
                title: getReportTitle(selectedReport, t),
              })}
              onLoad={() => setIframeLoading(false)}
            />
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default SealReportViewer;
